import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ImageIcon, MessageSquareText } from 'lucide-react'
import { api, type ApiRequestError, type CleaningAsk, type CleaningAskProblem } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import {
  DEFAULT_PROPERTY_TIME_ZONE,
  formatCleaningDay,
  localDate,
  localDateTimeToInstant,
  localTimeOfDay,
  shortTime,
} from './presentation'
import { useWaitingAsks } from './crew-asks-query'

/**
 * Crew asks (CAP-709/CAP-710): a crew message recorded as sent, waiting for
 * the crew's tapback, and answered once with the screenshot of it.
 *
 * The answer is all or nothing (Andrei, 2026-10-06): a tapback likes the whole
 * message, so recording it acks every cleaning the message listed. admin-api
 * refuses it, naming the jobs, if any changed since the ask - the fix is to
 * send the current list, which is a new ask.
 */

/** "Mon 6 Oct at 2:10pm", in the properties' zone like the rest of the board. */
function when(iso: string, today: string | null): string {
  const zone = DEFAULT_PROPERTY_TIME_ZONE
  const day = formatCleaningDay(localDate(iso, zone), today)
  const time = shortTime(iso, zone)
  return time ? `${day} at ${time}` : day
}

/**
 * What a "No ack" pill says instead while the job is in a crew message still
 * waiting on a reply: "Asked today". The same warning tone, because it is still
 * unacked, but "never told" and "told, waiting" stop looking alike - and it
 * takes the pill's place rather than sitting beside it, so a row does not grow
 * a second line in a column sized for one badge.
 */
export function AskedBadge({ sentAt, today }: { sentAt: string; today: string | null }) {
  return (
    <Badge
      variant="warning"
      title={`No ack yet. In a crew message sent ${when(sentAt, today)}, waiting on a reply.`}
    >
      <MessageSquareText className="mr-1 h-3 w-3" aria-hidden="true" />
      Asked{' '}
      {formatCleaningDay(localDate(sentAt, DEFAULT_PROPERTY_TIME_ZONE), today).replace(
        /^(Today|Yesterday|Tomorrow)$/,
        d => d.toLowerCase()
      )}
    </Badge>
  )
}

/**
 * The Needs action tab's "Waiting on crew" list. Renders nothing when nothing
 * is waiting, so the tab looks exactly as it did for a crew that acks on its
 * own links.
 */
export function WaitingAsks({ today }: { today: string | null }) {
  const query = useWaitingAsks()
  const [answering, setAnswering] = useState<CleaningAsk | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Every ask with a close in flight, not just the latest: one mutation
  // observer keeps only the most recent call's variables, so closing B while A
  // is still closing would otherwise re-enable A's actions mid-request.
  const [closing, setClosing] = useState<ReadonlySet<string>>(() => new Set())
  const close = useMutation({
    mutationFn: (ask: CleaningAsk) => api.closeCleaningAsk(ask.id),
    onMutate: ask => setClosing(current => new Set(current).add(ask.id)),
    onSettled: (_data, _error, ask) =>
      setClosing(current => {
        const next = new Set(current)
        next.delete(ask.id)
        return next
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['cleaning', 'asks'] }),
    onError: (error: ApiRequestError) =>
      toast({
        title: 'Could not close it',
        description: (error.body as { error?: string } | null)?.error ?? error.message,
        variant: 'destructive',
      }),
  })

  const asks = query.data?.asks ?? []
  const closingThis = (ask: CleaningAsk) => closing.has(ask.id)
  if (query.isError) {
    return (
      <p className="text-sm text-textMuted">
        The crew messages waiting on a reply could not be loaded.{' '}
        <button
          type="button"
          className="underline underline-offset-2 hover:no-underline"
          onClick={() => void query.refetch()}
        >
          Retry
        </button>
      </p>
    )
  }
  // Said even when nothing is listed: a clipped scan that found no waiting
  // asks is not a clean slate. The asks past the cap are the oldest, so this
  // is rare, but silence would be the wrong answer.
  const clipped = query.data?.truncated ?? false
  if (asks.length === 0 && !clipped) return null

  return (
    <section aria-labelledby="waiting-on-crew" className="space-y-2">
      <h2
        id="waiting-on-crew"
        className="text-[11px] font-semibold uppercase tracking-wider text-textFaint"
      >
        Waiting on crew
      </h2>
      {clipped && (
        <p className="text-xs text-amber">
          Not every crew message could be checked, so an older one still waiting on a reply may be
          missing here.
        </p>
      )}
      {asks.length > 0 && (
        <ul className="divide-y divide-border rounded-card border border-border bg-surface">
          {asks.map(ask => (
            <li key={ask.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text">
                  {ask.team_name}
                  <span className="ml-2 whitespace-nowrap font-normal text-textMuted">
                    {ask.items.length} {ask.items.length === 1 ? 'cleaning' : 'cleanings'}
                  </span>
                </p>
                <p className="text-xs text-textFaint">Sent {when(ask.sent_at, today)}</p>
              </div>
              <div className="flex items-center gap-2">
                {/* While this ask is being closed, neither action is offered:
                    a reply recorded during the close would race it. */}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={closingThis(ask)}
                  onClick={() => close.mutate(ask)}
                >
                  No reply
                </Button>
                <Button size="sm" disabled={closingThis(ask)} onClick={() => setAnswering(ask)}>
                  Record reply
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {answering && (
        <AskAnswerDialog ask={answering} today={today} onClose={() => setAnswering(null)} />
      )}
    </section>
  )
}

/** PNG and JPEG are what admin-api stores; a macOS screenshot pastes as PNG. */
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg']

function imageFromClipboard(event: React.ClipboardEvent): File | null {
  for (const item of Array.from(event.clipboardData?.items ?? [])) {
    if (item.kind === 'file' && ACCEPTED_IMAGE_TYPES.includes(item.type)) {
      return item.getAsFile()
    }
  }
  return null
}

/**
 * Record the crew's tapback on one ask: the screenshot, when they reacted, and
 * one button that acks every cleaning in the message.
 */
export function AskAnswerDialog({
  ask,
  today,
  onClose,
}: {
  ask: CleaningAsk
  today: string | null
  onClose: () => void
}) {
  const zone = DEFAULT_PROPERTY_TIME_ZONE
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [image, setImage] = useState<File | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)
  // Defaults to now (Andrei, 2026-10-06): the reply is usually recorded as it
  // arrives. Editable for one recorded later.
  const now = useMemo(() => new Date().toISOString(), [])
  const [date, setDate] = useState(localDate(now, zone) ?? '')
  const [time, setTime] = useState(localTimeOfDay(now, zone) ?? '')
  // Untouched, the fields show the moment the dialog opened, to the minute.
  // What is sent is that same moment WITH its seconds: rebuilding it from the
  // fields would land on :00, before a cleaning confirmed earlier in that
  // minute, and leaving it to admin-api would record when the button was
  // pressed - minutes after the time on screen if the dialog sat open.
  const [timeEdited, setTimeEdited] = useState(false)
  const editDate = (value: string) => {
    setDate(value)
    setTimeEdited(true)
    setProblems(null)
  }
  const editTime = (value: string) => {
    setTime(value)
    setTimeEdited(true)
    setProblems(null)
  }
  const [problems, setProblems] = useState<CleaningAskProblem[] | null>(null)
  // Inside the dialog, not only a toast: the toast lands behind the modal.
  const [answerError, setAnswerError] = useState<string | null>(null)

  const previewUrl = useMemo(() => (image ? URL.createObjectURL(image) : null), [image])
  useEffect(() => {
    if (!previewUrl) return
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const takeImage = (file: File | null | undefined) => {
    // Ignored mid-request: see `locked` below.
    if (!file || answer.isPending) return
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError('Paste or choose a PNG or JPEG screenshot.')
      return
    }
    setImageError(null)
    setImage(file)
  }

  const reactedAt = localDateTimeToInstant(date, time, zone)

  const answer = useMutation({
    mutationFn: async () => {
      if (!image || !reactedAt) throw new Error('A screenshot and a time are needed')
      const { proof_id } = await api.uploadCleaningAckProof(image)
      return api.answerCleaningAsk(ask.id, {
        proof_id,
        reacted_at: timeEdited ? reactedAt : now,
      })
    },
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: ['cleaning'] })
      toast({
        title: 'Reply recorded',
        description: `${result.acked + result.already_acked} ${
          result.acked + result.already_acked === 1 ? 'cleaning' : 'cleanings'
        } acknowledged by ${ask.team_name}`,
      })
      onClose()
    },
    onError: (error: ApiRequestError) => {
      const body = error.body as
        { problems?: CleaningAskProblem[]; error?: string } | null | undefined
      if (error.status === 409 && body?.problems?.length) {
        setProblems(body.problems)
        return
      }
      setAnswerError(body?.error ?? error.message)
      toast({
        title: 'Could not record the reply',
        // admin-api's own sentence ("reacted_at is in the future") over the
        // raw status line, when it sent one.
        description: body?.error ?? error.message,
        variant: 'destructive',
      })
    },
  })

  // Everything the answer is made of is frozen while it is being recorded:
  // the request already holds the values, so an edit made now would show one
  // thing on screen and record another.
  const locked = answer.isPending

  return (
    // Not dismissable mid-request: closing does not abort it, so the
    // cleanings would be acknowledged behind a dialog that looked cancelled.
    <Dialog open onOpenChange={open => !open && !answer.isPending && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-lg overflow-y-auto"
        onPaste={event => takeImage(imageFromClipboard(event))}
      >
        <DialogHeader>
          <DialogTitle>Record {ask.team_name}&rsquo;s reply</DialogTitle>
          <DialogDescription>
            Sent {when(ask.sent_at, today)}. Recording the reply acknowledges all {ask.items.length}{' '}
            {ask.items.length === 1 ? 'cleaning' : 'cleanings'} in it.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-1 rounded-md border border-border bg-surface2 p-3 text-sm">
          {ask.items.map(item => (
            <li key={`${item.property_id}:${item.job_id}`} className="flex justify-between gap-3">
              <span className="truncate text-text">{item.property}</span>
              <span className="shrink-0 tabular-nums text-textMuted">
                {when(item.scheduled_at, today)}
              </span>
            </li>
          ))}
        </ul>

        <details>
          <summary className="cursor-pointer text-xs text-textFaint">The message as sent</summary>
          <pre className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-surface2 p-3 text-xs text-textMuted">
            {ask.message}
          </pre>
        </details>

        <div className="space-y-2">
          <p className="text-sm font-medium text-text">Screenshot of the reply</p>
          {previewUrl ? (
            <div className="space-y-2">
              <img
                src={previewUrl}
                alt="The pasted screenshot of the crew's reply"
                className="max-h-56 rounded border border-border"
              />
              <Button size="sm" variant="ghost" onClick={() => setImage(null)} disabled={locked}>
                Remove
              </Button>
            </div>
          ) : (
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-borderStrong px-4 py-6 text-center text-sm text-textMuted hover:border-mint focus-within:border-mint">
              <ImageIcon className="h-5 w-5 text-textFaint" aria-hidden="true" />
              <span>
                Paste it here (<kbd className="font-sans">⌘V</kbd>) or choose a file
              </span>
              <input
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                disabled={locked}
                className="sr-only"
                onChange={event => takeImage(event.target.files?.[0])}
              />
            </label>
          )}
          {imageError && <p className="text-xs text-danger">{imageError}</p>}
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium text-text">When they reacted (ET)</p>
          <div className="flex gap-2">
            <input
              type="date"
              aria-label="Reaction date"
              disabled={locked}
              value={date}
              onChange={event => editDate(event.target.value)}
              className="h-9 rounded-input border border-border bg-surface2 px-2 text-sm text-text"
            />
            <input
              type="time"
              aria-label="Reaction time"
              disabled={locked}
              value={time}
              onChange={event => editTime(event.target.value)}
              className="h-9 rounded-input border border-border bg-surface2 px-2 text-sm text-text"
            />
          </div>
        </div>

        {answerError && !problems && (
          <p className="text-sm text-danger" role="alert">
            {answerError}
          </p>
        )}

        {problems && (
          <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm" role="alert">
            <p className="font-medium text-text">
              Nothing was recorded. The reply cannot cover these cleanings:
            </p>
            <ul className="mt-1 list-disc pl-5 text-textMuted">
              {problems.map(problem => (
                <li key={`${problem.property_id}:${problem.job_id}`}>
                  {problem.property} {problem.reason}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-textMuted">
              If one changed since the message was sent, send {ask.team_name} the current list from
              Message a crew and record the reply to that one. If the reaction time is wrong,
              correct it above and try again.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={answer.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setAnswerError(null)
              setProblems(null)
              answer.mutate()
            }}
            // Once refused, the same answer would be refused again. Editing
            // the time clears the refusal, since a time before a cleaning was
            // confirmed is a refusal the operator can correct here.
            disabled={!image || !reactedAt || answer.isPending || problems !== null}
          >
            {answer.isPending
              ? 'Recording…'
              : `Acknowledge ${ask.items.length} ${ask.items.length === 1 ? 'cleaning' : 'cleanings'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
